<template>
  <div
    v-click-outside="onClickOutside"
    class="c-date-picker"
    :class="classes"
  >
    <div class="border" />

    <div class="value" tabindex="0" @click="opened = !opened">
      <c-flag
        v-if="icon"
        grey
        class="flag"
        size="30"
        icon-size="18"
        :icon="icon"
      />

      <c-flag
        v-if="expiredDate || alertDateColor"
        class="icon-alert-date"
        validation
        :alert="alertDateColor"
      />

      <input
        v-if="enableInput && mode === 'single'"
        ref="input"
        v-mask="'##/##/####'"
        type="text -input"
        :class="inputClasses"
        :value="inputValue"
        :placeholder="placeholder"
        :data-testid="`date-picker-${fieldName}`"
        @input.stop="onInput"
        @keyup.enter.stop.prevent
        @keydown.enter.stop.prevent
        @keypress.enter.stop.prevent
      />

      <c-transition v-else mode="out-in" duration="100">
        <span
          :key="displayedValue"
          :class="textClasses"
        >
          {{ displayedValue }}
        </span>
      </c-transition>

      <c-icon
        class="select-icon"
        icon="arrow-right-2"
        size="24"
      />
    </div>

    <div class="wrapper">
      <transition name="content">
        <div v-if="opened" class="content">
          <c-button
            class="redefine-btn"
            type="button"
            flat
            :size="35"
            @click.stop="onReset"
          >
            Redefinir
          </c-button>

          <c-calendar
            v-bind="calendarProps"
            ref="calendar"
            wide
            :panes="1"
            :value="internalValue"
            :mode="mode"
            :max-date="maxDate ? new Date(maxDate) : undefined"
            :min-date="minDate ? new Date(minDate) : undefined"
            :disabled-dates="disabledDates"
            @input="onSelect"
            @preselect="$emit('preselect', $event)"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import CFlag from '@convenia/components/CFlag'
import CIcon from '@convenia/components/CIcon'
import CButton from '@convenia/components/CButton'
import CCalendar from '@convenia/components/CCalendar'
import CTransition from '@convenia/components/CTransition'
import clickOutside from '@convenia/components/directives/modules/clickOutside'
import { maska as mask } from 'maska'
import { is, isEmptyObject } from '@convenia/helpers'
import { dayjs, tz } from '@convenia/utils'

export default {
  name: 'CDatePicker',

  directives: { clickOutside, mask },

  components: {
    CFlag,
    CTransition,
    CIcon,
    CButton,
    CCalendar,
  },

  props: {
    /**
     * The icon to show field.
     */
    icon: {
      type: String,
      default: 'calendar-schedule-2'
    },

    /**
     * The placeholder of the field.
     */
    placeholder: {
      type: String,
      default: 'Selecione a data'
    },

    /**
     * The value of the field.
     *
     * set to an Array of Dates when mode is 'multiple':
     * @type { Date[] }
     *
     * set to an Object with the following format when mode is 'range':
     * @type { { start: Date, end: Date } }
     */
    value: {
      type: [ Object, String, Date, Array ],

      // This default is set just to avoid problems with storybook
      default: () => ({})
    },

    /**
     * The lower limit selectable date.
     */
    minDate: Date,

    /**
     * The upper limit selectable date.
     */
    maxDate: Date,

    /**
     * The date selecting mode
     * The validation array is self explanatory.
     */
    mode: {
      type: String,
      default: 'single',
      validator: mode => [ 'range', 'single', 'multiple' ].includes(mode),
    },

    /**
     * Disables the field
     */
    disabled: Boolean,

    /**
     * Displays a validation error highlight
     */
    error: Boolean,

    /**
     * A date expression of disabled dates in the calendar
     * Check https://vcalendar.io/date-expressions.html
     */
    disabledDates: {
      type: Object,
      default: null,
    },

    /**
     * Makes the input area editable
     */
    enableInput: Boolean,

    /**
     * Displays if the date is expired by displaying an alert icon
     */
    expiredDate: Boolean,

    /**
     * Changes the color of the date to alert color
     */
    alertDateColor: {
      type: Boolean,
    },

    /**
     * The field name
     */
    fieldName: {
      type: String,
      default: ''
    },

    /**
     * Props for the calendar component
     */
    calendarProps: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      opened: false,
      inputChanged: false,
      inputValue: undefined,
      internalValue: undefined
    }
  },

  emits: [
    'input',
    'focus',
    'blur',
    'preselect',
  ],

  watch: {
    opened (val) {
      this.$emit(val ? 'focus' : 'blur')
    },

    value: {
      immediate: true,
      handler: 'setInternalValue'
    },
  },

  computed: {
    classes () {
      return {
        '-opened': this.opened,
        '-disabled': this.disabled,
        '-has-icon': this.icon,
        '-validation': this.error,
        '-expired': this.expiredDate,
        '-alert-message': this.alertDateColor
      }
    },

    inputClasses () {
      return [ 'text', {
        '-placeholder': !this.enableInput && !this.internalValue,
        '-input': this.enableInput,
        '-expired': this.expiredDate,
        '-alert-message': this.alertDateColor
      } ]
    },

    textClasses () {
      return [ 'text', {
        '-placeholder': this.hasPlaceholder,
        '-expired': this.expiredDate,
        '-alert-message': this.alertDateColor
      } ]
    },

    displayedValue () {
      if (!this.internalValue) return this.placeholder
      const isObject = is(this.internalValue, 'Object')
      const hasStart = (this.internalValue || {}).start
      const hasEnd = (this.internalValue || {}).end
      const isMultiple = Array.isArray(this.internalValue) && this.mode === 'multiple'

      if (this.mode === 'range' && isObject && hasStart && hasEnd) {
        const startDate = this.internalValue.start.toLocaleDateString('pt-BR')
        const endDate = this.internalValue.end.toLocaleDateString('pt-BR')
        return `${startDate} - ${endDate}`
      }

      if (isMultiple && this.internalValue.length)
        return this.internalValue
          .filter(date => is(date, 'Date'))
          .map(date => date.toLocaleDateString('pt-BR'))
          .join(', ')

      if (this.mode === 'single' && is(this.internalValue, 'Date'))
        return this.internalValue.toLocaleDateString('pt-BR')

      return this.placeholder
    },

    hasPlaceholder () {
      const { internalValue } = this
      if (Array.isArray(internalValue) && this.mode === 'multiple')
        return !internalValue.length

      if (is(internalValue, 'Object') && this.mode === 'single')
        return !Object.keys(internalValue).length

      return !this.internalValue
    }
  },

  methods: {
    /**
     * parses a date from v-calendar selection into a consistent Date object.
     * uses noon to avoid day boundary issues across timezones.
     */
    parseDate (date) {
      if (!date) return new Date()

      const d = new Date(date)
      const dateString = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0')
      ].join('-')

      return dayjs.tz(`${dateString} 12:00:00`, 'YYYY-MM-DD HH:mm:ss', 'America/Sao_Paulo').toDate()
    },

    parseDateList (dates = []) {
      return (Array.isArray(dates) ? dates : [])
        .filter(Boolean)
        .map(date => this.parseDate(date))
    },

    formatDate (date) {
      return dayjs(date).tz(tz.guess()).format('DD/MM/YYYY')
    },

    normalizeDate (value) {
      if (!value) return null

      return dayjs.tz(`${value} 20:00:00`, 'DD/MM/YYYY HH:mm:ss', tz.guess()).toDate()
    },

    onSelect (value) {
      if (this.mode === 'range') this.internalValue = {
        start: value.start,
        end: value.end
      }

      if (this.mode === 'single')
        this.internalValue = this.parseDate(value)

      if (this.mode === 'multiple')
        this.internalValue = this.parseDateList(value)

      this.$emit('input', this.internalValue)
      this.setInputValue()

      if (this.mode !== 'multiple')
        this.opened = false
    },

    setInputValue () {
      if (this.mode !== 'single') return
      if (!this.internalValue) return
      const val = this.formatDate(this.internalValue)

      if (val === this.inputValue || val === 'Invalid Date') return
      this.inputValue = val
    },

    setInternalValue (val) {
      /**
       * For now we are only dealing with the 'range' mode
       * which expects the value to be an object with
       * 'start' and 'end' keys
       */
      if (Array.isArray(val))
        this.internalValue = this.parseDateList(val)

      else if (is(val, 'Object') && val.start && val.end)
        this.internalValue = { ...val }

      else if (val && is(val, 'String') && val.match(/[0-9]*-[0-9]*-[0-9]*/))
        // Parse date string at noon in São Paulo to avoid day boundary issues
        this.internalValue = dayjs.tz(`${val} 12:00:00`, 'YYYY-MM-DD HH:mm:ss', 'America/Sao_Paulo').toDate()

      else
        this.internalValue = val

      if (this.enableInput && this.mode === 'single' && !this.inputChanged)
        this.setInputValue()

      this.inputChanged = false
    },

    isDateValid (dateString) {
      const date = dayjs(this.normalizeDate(dateString))

      if (!dateString || !date.isValid()) return false

      const tooBig = this.maxDate && date > dayjs(this.maxDate)
      const tooSmall = this.minDate && date < dayjs(this.minDate)
      const same = date === dayjs(this.internalValue)

      if (same || tooBig || tooSmall) return false

      return true
    },

    updateValue ({ inputChanged = true, inputValue = '', internalValue = '' } = {}) {
      this.inputChanged = inputChanged
      this.inputValue = inputValue
      this.internalValue = internalValue

      this.$emit('input', internalValue)
    },

    async onInput (ev) {
      if (this.mode !== 'single') return
      const { value = '' } = ev.target || {}

      if (!value) {
        this.updateValue()
        return
      }

      if (value.length < 10) return
      const [ day, month, year ] = value.split('/')

      let date = this.internalValue && !isEmptyObject(this.internalValue)
        ? dayjs(this.internalValue)
        : dayjs()

      const calendar = this.$refs.calendar || {}
      const { controller } = calendar || {}
      const { input } = this.$refs || {}

      const isDayValid = (day || '').length === 2 && +day > 0 && +day < 32
      const isMonthValid = (month || '').length === 2 && +month > 0 && +month < 13
      const isYearValid = (year || '').length === 4 && +year > 0

      if (isYearValid)
        date = date.set('year', +year)
      if (isMonthValid)
        date = date.set('month', +month - 1)
      if (isDayValid)
        date = date.set('date', +day)

      if (!(value || '').trim() || (!isDayValid && !isMonthValid && !isYearValid))
        return

      this.updateValue({ inputValue: value, internalValue: this.parseDate(date.toDate()) })

      if (controller && this.opened) {
        await controller.focusDate(this.internalValue)
        this.$nextTick(() => { input.focus() })
      }
    },

    onReset () {
      const calendar = this.$refs.calendar || {}
      const { controller } = calendar || {}

      this.updateValue({ internalValue: this.mode === 'multiple' ? [] : '' })
      this.opened = false
      if (controller) controller.focusDate(new Date())
    },

    onClickOutside () {
      this.opened = false

      if (!this.inputChanged)
        this.setInternalValue(this.value)

      this.inputChanged = false
    }
  }
}
</script>

<style lang="scss">
@import '../styles/mixins/calendar-picker-shell';

.c-date-picker {
  $calendar-max-width: 276px;
  $content-height: 350px;
  $initial-height: 40px;
  $initial-border-radius: 20px;
  $open-border-radius: 5px;

  @include calendar-picker-shell(
    $content-height,
    $initial-height,
    $initial-border-radius,
    $open-border-radius,
    '.icon-alert-date',
    true,
    true
  );

  & > .value {
    & > .flag > .c-icon { position: relative; top: -1px; }

    & > .text {
      @include typo(body-4, base-50);

      &:not(.-input) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &.-placeholder, &.-input::placeholder { @include typo(body-1, base-30); }

      &.-input {
        &.-expired::placeholder { color: color-var(negative); }
        &.-alert-message::placeholder { color: color-var(alert); }
      }

      &.-input {
        border: 0;
        outline: 0;
        transition: color 200ms;
        background: transparent;
        width: 100%;

        &:hover,
        &:focus { color: color-var(text, base-80); }
      }

      &.-expired {
        color: color-var(negative);

        &:hover,
        &:focus { color: color-var(negative); }
      }

      &.-alert-message {
        color: color-var(alert);

        &:hover,
        &:focus { color: color-var(alert); }
      }
    }
  }

  & > .wrapper {
    & > .content {
      position: relative;
      height: $content-height;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      & > .redefine-btn {
        margin: 10px 0 15px;
        width: 120px;
      }

      & > .apply-btn {
        position: absolute;
        bottom: 0;
        left: 0;
      }
    }
  }
}
</style>
